import { VariableType } from 'json-to-graphql-query';
import { Resource } from './resource';
import { LoginVariables, LoginArgs } from './queryVariables/login';
import { ConfirmVariables, ConfirmArgs } from './queryVariables/confirm';
import { PermissionsVariables, PermissionsArgs } from './queryVariables/checkPermissions';
import { ResetVariables, ResetArgs } from './queryVariables/reset';
import { LoginWithDeviceVariables, LoginWithDeviceArgs } from './queryVariables/loginWithDevice';
import { ConfirmManuallyArgs, ConfirmManuallyVariables } from './queryVariables/confirmManually';
/**
 * Resource autentykacji
 *
 * @export
 * @class AuthResource
 * @extends {Resource}
 */
export class AuthResource extends Resource {
    /**
     * Logowanie
     *
     * @param {string} email
     * @param {string} password
     * @param {boolean} [rememberMe]
     * @return {Promise<{ token: string; profile: Profile; hasAllConsents: boolean }>}
     * @memberof AuthResource
     */
    login(email, password, rememberMe) {
        const query = this.prepareOptions({
            query: {
                __variables: { ...LoginVariables },
                accountLogin: {
                    __args: { ...LoginArgs },
                    token: true,
                    hasAllConsents: true,
                    profile: {
                        id: true,
                        type: true,
                        firstName: true,
                        lastName: true,
                        userId: true,
                        needsPasswordChange: true,
                        groups: {
                            id: true,
                            data: {
                                type: true
                            },
                            name: true,
                            auto: true
                        },
                        partner: {
                            id: true,
                            name: true
                        }
                    }
                }
            }
        });
        return this.query({
            query,
            variables: { login: email, password, rememberMe }
        }, 'accountLogin');
    }
    /**
     * Logowanie z zapisem urządzenia użytkownika
     *
     * @param {string} email
     * @param {string} password
     * @param {object} deviceInput
     * @param {boolean} rememberMe
     * @returns {Promise<{ token: string; profile: Profile; hasAllConsents: boolean }>}
     * @memberof AuthResource
     */
    loginWithDevice(email, password, deviceInput, rememberMe) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { ...LoginWithDeviceVariables },
                accountLoginWithDevice: {
                    __args: { ...LoginWithDeviceArgs },
                    token: true,
                    hasAllConsents: true,
                    profile: {
                        id: true,
                        type: true,
                        firstName: true,
                        lastName: true,
                        userId: true,
                        needsPasswordChange: true,
                        groups: {
                            id: true,
                            data: {
                                type: true
                            },
                            name: true,
                            auto: true
                        },
                        partner: {
                            id: true,
                            name: true
                        }
                    }
                }
            }
        });
        return this.mutation({
            mutation,
            variables: { login: email, password, deviceInput, rememberMe }
        }, 'accountLoginWithDevice');
    }
    /**
     * Potwierdzenie konta
     *
     * @param {string} token
     * @param {string} password
     * @returns {Promise<number>}
     * @memberof AuthResource
     */
    async confirm(token, password) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: ConfirmVariables,
                accountConfirm: {
                    __args: ConfirmArgs
                }
            }
        });
        return this.mutation({
            mutation,
            variables: { token, password }
        }, 'accountConfirm');
    }
    /**
     * Ręczne potwierdzanie konta
     *
     * @param {string} userId
     * @param {string} password
     * @returns {Promise<number>}
     * @memberof AuthResource
     */
    confirmManually(userId, password) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: ConfirmManuallyVariables,
                accountConfirmManually: {
                    __args: ConfirmManuallyArgs
                }
            }
        });
        return this.mutation({
            mutation,
            variables: { userId, password }
        }, 'accountConfirmManually');
    }
    /**
     * Resetowanie hasła
     *
     * @param {string} email
     * @returns {Promise<number>}
     * @memberof AuthResource
     */
    async resetPassword(email) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: ResetVariables,
                accountResetPassword: {
                    __args: ResetArgs
                }
            }
        });
        return this.mutation({
            mutation,
            variables: { email }
        }, 'accountResetPassword');
    }
    /**
     * Ustawienie hasła
     *
     * @param {string} token
     * @param {string} password
     * @returns {Promise<number>}
     * @memberof AuthResource
     */
    async setPassword(token, password) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: ConfirmVariables,
                accountSetPassword: {
                    __args: ConfirmArgs
                }
            }
        });
        return this.mutation({
            mutation,
            variables: { token, password }
        }, 'accountSetPassword');
    }
    /**
     * Sprawdzenie uprawnien
     *
     * @param {string[]} permissions
     * @returns {Promise<boolean>}
     * @memberof AuthResource
     */
    async checkPermission(permissions) {
        const query = this.prepareOptions({
            query: {
                __variables: PermissionsVariables,
                checkPermissions: {
                    __args: PermissionsArgs
                }
            }
        });
        return this.query({
            query,
            variables: { permissions }
        }, 'checkPermissions');
    }
    /**
     * Odświeżenie tokena
     *
     * @param {boolean} [rememberMe]
     * @returns {Promise<string>}
     * @memberof AuthResource
     */
    async refreshToken(rememberMe) {
        const query = this.prepareOptions({
            query: {
                __variables: {
                    rememberMe: 'Boolean'
                },
                accountRefreshToken: {
                    __args: { rememberMe: new VariableType('rememberMe') }
                }
            }
        });
        return this.query({ query, variables: { rememberMe } }, 'accountRefreshToken', true);
    }
    /**
     * Sprawdzanie czy login jest zajęty
     *
     * @param {string} login
     * @returns {Promise<boolean>}
     * @memberof AuthResource
     */
    async checkLoginAvailability(login) {
        const query = this.prepareOptions({
            query: {
                __variables: {
                    login: 'String!'
                },
                accountIsAvailable: {
                    __args: { login: new VariableType('login') }
                }
            }
        });
        return this.query({ query, variables: { login } }, 'accountIsAvailable');
    }
    /**
     * Ponowne wysłanie linku aktywacyjnego
     *
     * @param {string} login
     * @returns {Promise<boolean>}
     * @memberof AuthResource
     */
    sendConfirm(email) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { email: 'String!' },
                accountSubmitConfirm: {
                    __args: { email: new VariableType('email') }
                }
            }
        });
        return this.mutation({ mutation, variables: { email } }, 'accountSubmitConfirm');
    }
    /**
     * Zmiana hasła
     *
     * @param {{ newPassword: string; userId: string; password?: string }} variables
     * @returns {Promise<boolean>}
     * @memberof AuthResource
     */
    changePassword(variables) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { newPassword: 'String!', userId: 'String!', password: 'String!' },
                accountChangePassword: {
                    __args: {
                        newPassword: new VariableType('newPassword'),
                        userId: new VariableType('userId'),
                        password: new VariableType('password')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables }, 'accountChangePassword');
    }
}
export const authResource = new AuthResource();
//# sourceMappingURL=auth.resource.js.map